// dependencies
import React, { ReactElement, memo, ReactNode, useCallback, useContext } from "react";
import { clickMenuBack } from "@cart-client/analytics/events";

// styles
import cx from "classnames";
import styles from "./Header.scss";
import { withStyles } from "@swiggy-private/core-ui";

// hooks
import { GroupOrderCartContext, useOnBack } from "@swiggy-private/checkout-core";

// constants
import { COMMON_HEADER_TEST_IDS } from "@swiggy-private/cart-test-ids";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

//types
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";

//helper
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

interface CommonHeaderProps {
    children: ReactNode;
    onBack?: VoidFunction;
    containerClass?: string;
    isVisible: boolean;
    isPageScrolled?: boolean;
    showAddressWidgetTop?: boolean;
    isEmptyCart: boolean;
}

export const ACCESSIBILITY_TEXTS = {
    GO_BACK: "Go Back",
};

function BackButtonComponentv4(props: {
    className?: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}): ReactElement {
    return (
        <button
            aria-label={ACCESSIBILITY_TEXTS.GO_BACK}
            className={props.className}
            onClick={props.onClick}
            data-testid={COMMON_HEADER_TEST_IDS.BACK_BUTTON}
        >
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    opacity="0.9"
                    d="M6.81875 14.5714L1 8.23808M1 8.23808L6.81875 1.90475M1 8.23808L19 8.23808"
                    stroke="var(--arrow-color-env, #66686E)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
}

const BackButtonv4 = React.memo(BackButtonComponentv4);

function CommonHeader(props: CommonHeaderProps): ReactElement {
    const { isEmptyCart, onBack: defaultOnBack } = props;
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { clickEvent } = useContext(AnalyticsContext);
    const { state: groupCartData } = useContext(GroupOrderCartContext);
    const { data } = useContext(CartFlowContext);
    const isGiftingCouponListing = isGiftingCartSource(data?.cartSource);

    const onBack = useOnBack();
    const containerClassName = cx(styles.container, props.containerClass, {
        [styles.containerNotVisible]: !props.isVisible,
        [styles.containerGrayBackground]: !props.showAddressWidgetTop && !props.isPageScrolled,
    });

    const onBackButtonClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();

            clickMenuBack(clickEvent, cartState?.localCart?.cart_id?.toString(), groupCartData?.data?.group_cart_id, {
                basePayload: {
                    restaurantId: cartResponse?.data?.restaurant_details?.id,
                    originalCartId: (cartResponse as GiftingReviewedCart)?.data?.originalCartId,
                    cartId: (cartResponse as GiftingReviewedCart)?.data?.cartId,
                },
                isGiftingCouponListing,
                ...(isEmptyCart && { isEmptyCart }),
            });

            updateStatusBarCleanup();

            if (typeof defaultOnBack === "function") {
                defaultOnBack();
                return;
            }
            onBack();
        },
        [
            cartResponse,
            cartState?.localCart?.cart_id,
            clickEvent,
            groupCartData?.data?.group_cart_id,
            isGiftingCouponListing,
            onBack,
            defaultOnBack,
            isEmptyCart,
        ],
    );

    const BackButton = BackButtonv4;

    const backButtonClassName = cx(styles.containerIcon, {
        [styles.containerIconScrolled]: props.isPageScrolled && props.showAddressWidgetTop,
    });

    return (
        <div className={containerClassName} data-testid={COMMON_HEADER_TEST_IDS.CART_HEADER}>
            <BackButton onClick={onBackButtonClick} className={backButtonClassName} />
            <div className={styles.containerContent}>{props.children}</div>
        </div>
    );
}

export default memo(withStyles(CommonHeader, styles));
